import { useMutation, useQuery } from '@tanstack/react-query';
import { backendServices } from 'common';
import { useCompanyPublicData } from '../../../services';
import { SupplierSignUpData } from './SupplierSignUpSchema';
import { SupplierSignUpService } from './SupplierSignUpService';

function useRoleTypes() {
    const query = useQuery(
        ['RoleTypes'],
        async () => {
            const api = new backendServices.Apis.RoleTypeApi();

            const res = await api.getRoleTypes({});

            return res.data;
        },
        { cacheTime: Infinity, staleTime: Infinity }
    );

    return query;
}

function useCompanyProfile({ buyerCompanyId }: { buyerCompanyId: number | null }) {
    const query = useQuery(
        ['CompanyProfile', buyerCompanyId],
        async () => {
            const api = new backendServices.Apis.ProfileApi();
            const res = await api.getProfileSearch({
                profileType: backendServices.ViewModels.ProfileType.BuyerDestination,
                companyId: buyerCompanyId!,
            });

            return res.data[0]?.ProfileExtendedDatas ?? [];
        },
        { cacheTime: Infinity, staleTime: Infinity, enabled: buyerCompanyId != null }
    );

    return query;
}

export function extractAPIErrorMessage(e: unknown) {
    if (e instanceof Error) {
        return e.message.split('API-ERROR:')[1] ?? null;
    }
    return null;
}

export function mapApiErrorToDisplayMsg(error: string) {
    switch (true) {
        case error.trim().includes('Same TIN, Email Phone or Fax in Company Ids'):
            return "One of the following fields: 'TIN', 'Email', 'Phone' or 'Fax' you have entered for the company already exists. If your company is already registered, please sign in to make changes to your account.";
        case error.trim().includes('A user with the same email address is registered in the system.'):
            return 'A user with the same email address is registered in the system. Please use a different email address. You can also contact Transcepta support at 949-382-2841 or submit a ticket at registration@transcepta.com if you need further assistance.';
        case error.trim().includes('Same Name or DUNS number in Company Ids'):
            return 'Another company exists with the same name or DUNS. You can reach out to Transcepta support at 949-382-2841 or submit a ticket at registration@transcepta.com if you need further assistance.';

        default:
            return error;
    }
}

export type PostSupplierCompanyResult = Awaited<
    ReturnType<InstanceType<typeof SupplierSignUpService>['createSupplier']>
>['results'][0];

export function usePostSupplierCompany({
    searchToken,
    onSuccess,
}: {
    searchToken: string;
    onSuccess: (result: PostSupplierCompanyResult | undefined) => void;
}) {
    const { data: company, isLoading: isLoadingCompanyData } = useCompanyPublicData({ search: searchToken });

    const buyerCompanyId = company?.ID ?? null;

    const { data: roleTypesResponse, isLoading: isLoadingRoles, error: rolesError } = useRoleTypes();

    const roleTypes = roleTypesResponse?.Items;

    const {
        data: profileExtendedData,
        isLoading: isLoadingProfileData,
        error: profileError,
    } = useCompanyProfile({ buyerCompanyId });

    const isLoading = isLoadingCompanyData || isLoadingProfileData || isLoadingRoles;

    const mutation = useMutation({
        mutationFn: async ({
            supplierData,
            submissionMethod,
        }: {
            supplierData: SupplierSignUpData;
            submissionMethod: Pick<backendServices.ViewModels.SubmissionMethodViewModel, 'SubmissionMethod'>;
        }) => {
            if (profileError) {
                throw profileError;
            }

            if (rolesError) {
                throw rolesError;
            }

            if (isLoading || company == null || roleTypes == null || profileExtendedData == null) {
                throw new Error('Supplier signup data is not ready.');
            }

            const service = new SupplierSignUpService(profileExtendedData, company, roleTypes);

            const res = await service.createSupplier(supplierData, submissionMethod.SubmissionMethod);

            const result = res.results[0];

            if (result.CompanyId <= 0) {
                throw new Error(`API-ERROR:${result.Message ?? ''}`);
            }

            return result;
        },
        onSuccess: (result) => {
            onSuccess(result);
        },
    });

    return mutation;
}
